/**
 * Translation. Deliberately small: the app has one real language today (ES)
 * and a second one to keep honest (EN). A library would add weight without
 * adding anything this does not already do.
 *
 * Product copy lives here. Behavior names and anchors come from the API,
 * because they are client-editable content rather than interface strings.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_LOCALE, LOCALES, type Locale } from '@genoma/shared';

import { en } from './en.ts';
import { es } from './es.ts';

/**
 * Spanish is the reference: every other locale must satisfy its *shape*.
 * The mapped type replaces each literal with `string`, so a translation is
 * checked for completeness without being required to repeat the Spanish text.
 */
type Translated<T> = { [K in keyof T]: T[K] extends string ? string : Translated<T[K]> };
export type Messages = Translated<typeof es>;

const catalogues: Record<Locale, Messages> = { es, en };

const STORAGE_KEY = 'genoma.locale';

/**
 * What language to paint before the session arrives.
 *
 * Whatever this person last chose, and Spanish otherwise. It used to fall back
 * to `navigator.language`, which meant the product spoke whichever language
 * somebody's browser happened to be set to — so two people at the same company
 * saw two different applications and neither had chosen it. The product's
 * language is Spanish; the browser's is not evidence of anything.
 *
 * Once the session loads, the person's own stored preference wins over this.
 */
const readStoredLocale = (): Locale => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored && (LOCALES as readonly string[]).includes(stored)
    ? (stored as Locale)
    : DEFAULT_LOCALE;
};

type I18nValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Look up a dotted key, interpolating {placeholders}. */
  t: (key: string, values?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

const resolve = (messages: Messages, key: string): string => {
  const value = key
    .split('.')
    .reduce<unknown>((node, part) => (node as Record<string, unknown>)?.[part], messages);
  // A missing key shows itself rather than rendering an empty gap.
  return typeof value === 'string' ? value : key;
};

const interpolate = (template: string, values?: Record<string, string | number>): string =>
  values
    ? template.replace(/\{(\w+)\}/g, (match, name) => String(values[name] ?? match))
    : template;

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale: (next) => {
        localStorage.setItem(STORAGE_KEY, next);
        document.documentElement.lang = next;
        setLocaleState(next);
      },
      t: (key, values) => interpolate(resolve(catalogues[locale], key), values),
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nValue => {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside an I18nProvider');
  return context;
};

/** Shorthand for components that only need the translate function. */
export const useTranslate = () => useI18n().t;
